import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const SizeBreakInput = ({
  className,
  placeholderText,
  sizeBreakId,
  sizeBreakType,
  sizeBreakSize,
  isDisabled,
  isReadonly,
  defaultValue,
  localComputeKey = 0,
  title,
  updateSizeBreakQty,
  handleSubmission,
  sizeBreakKey
}) => {
  const [inputValue, setInputValue] = useState(defaultValue ?? "");
  const pendingUpdateRef = useRef(false);
  const initialValueRef = useRef(defaultValue ?? "");
  const lastValidValueRef = useRef(defaultValue ?? "");
  const isOptimisticRef = useRef(false);
  const timeoutRef = useRef(null);

  // Keep a ref to the latest defaultValue so the localComputeKey effect can
  // read it synchronously without needing it as a dependency.
  const defaultValueRef = useRef(defaultValue);
  useEffect(() => {
    defaultValueRef.current = defaultValue;
  });

  useEffect(() => {
    setInputValue(defaultValue ?? "");
    lastValidValueRef.current = defaultValue ?? "";
    initialValueRef.current = defaultValue ?? "";
  }, []);

  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (!isOptimisticRef.current && defaultValue !== undefined) {
      setInputValue(defaultValue);
      lastValidValueRef.current = defaultValue;
      initialValueRef.current = defaultValue;
    } else if (defaultValue === undefined || defaultValue === null) {
      // Reset isOptimisticRef when value is nil (null or undefined)
      isOptimisticRef.current = false;
    }
  }, [defaultValue]);

  // When a sibling field causes a computed update to this field (e.g. editing
  // fill recalculates stock), localComputeKey is incremented by the reducer.
  // We detect the increase and accept the new computed value even if
  // isOptimisticRef is still true from a previous edit of THIS field.
  // Server responses never increment localComputeKey, so the optimistic lock
  // still protects against stale server overwrites.
  const prevLocalComputeKeyRef = useRef(localComputeKey);
  useEffect(() => {
    if (localComputeKey > prevLocalComputeKeyRef.current) {
      isOptimisticRef.current = false;
      const currentDefault = defaultValueRef.current;
      if (currentDefault !== undefined) {
        setInputValue(currentDefault);
        lastValidValueRef.current = currentDefault;
        initialValueRef.current = currentDefault;
      }
    }
    prevLocalComputeKeyRef.current = localComputeKey;
  }, [localComputeKey]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const validateInput = (value) => {
    if (value === "") return { isValid: true, value: "" };

    const numValue = parseInt(value);
    if (isNaN(numValue)) return { isValid: false };
    if (numValue < 0 || numValue > 99999) return { isValid: false };

    return { isValid: true, value: numValue };
  };

  const handleChange = (evt) => {
    const targetValue = evt.target.value;
    const validation = validateInput(targetValue);

    if (!validation.isValid && targetValue !== "") {
      return;
    }

    setInputValue(targetValue);
    isOptimisticRef.current = true;

    const syntheticEvent = {
      target: {
        value: targetValue,
        dataset: {
          sizeBreakId,
          sizeBreakType,
          sizeBreakSize,
          sizeBreakKey
        }
      }
    };

    updateSizeBreakQty(syntheticEvent);
    pendingUpdateRef.current = true;

    // Reset isOptimisticRef if the value becomes empty
    if (!targetValue && targetValue !== 0) {
      isOptimisticRef.current = false;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleBlur = () => {
    if (!pendingUpdateRef.current) return;

    lastValidValueRef.current = inputValue;
    initialValueRef.current = inputValue;

    const syntheticEvent = {
      target: {
        value: inputValue,
        dataset: {
          sizeBreakId,
          sizeBreakType,
          sizeBreakSize,
          sizeBreakKey
        }
      }
    };

    handleSubmission(syntheticEvent);
    pendingUpdateRef. current = false;

    // Reset isOptimisticRef after blur to ensure fresh state for next defaultValue changes
    if (!inputValue && inputValue !== 0) {
      isOptimisticRef.current = false;
    }
  };

  return (
    <input
      type="text"
      inputMode='numeric'
      pattern='[0-9]*'
      title={title}
      className={`${className}`}
      name={`size_breaks[${sizeBreakId}][${sizeBreakType}]`}
      placeholder={placeholderText}
      disabled={isDisabled}
      readOnly={isReadonly}
      onPaste={(e) => e.preventDefault()}
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      data-size-break-id={sizeBreakId}
      data-size-break-type={sizeBreakType}
      data-size-break-size={sizeBreakSize}
      data-size-break-key={sizeBreakKey}
    />
  );
};

const StyledSizeBreakInput = styled(SizeBreakInput)`
  width: 24px;
  height: 22px;
  font-size: 10px;
  padding: 0 1px;
  text-align: center;
  color: #000000;
  font-weight: 400;
  background-color: #f9f9f9;
  box-shadow: none;
  border-radius: 0;
  font-family: "Roboto";
  margin: 0;
  line-height: 20px;
  border: 1px solid #ccc;
  vertical-align: middle;
  display: inline-block;
  cursor: ${props => (props.disabled === true ? "not-allowed" : "unset")};
  ${props => props.lowQty && "border: 1px solid var(--low-qty-border-color)"};
  ${props => props.markRed && "border: 1px solid var(--error-border-color)"};
  ${props => props.qtyExceeded && "border: 2px solid var(--qty-exceeded-border-color)"};

  ::placeholder {
    color: #d5d5d5;
  }

  &:focus {
    border-color: var(--bs-dark-black);
  }
`;

export default React.memo(StyledSizeBreakInput);
